import service from '@/utils/https'

function accountingEntriesQueryParams(params = {}) {
  const queryParams = new URLSearchParams()
  if (params.status) queryParams.append('status', params.status)
  if (params.type) queryParams.append('type', params.type)
  if (params.freelancer) queryParams.append('freelancer', params.freelancer)
  if (params.period) queryParams.append('period', params.period)
  if (params.accounting_send) queryParams.append('accounting_send', params.accounting_send)
  if (params.napta_export) queryParams.append('napta_export', params.napta_export)
  if (params.team_id) queryParams.append('team_id', params.team_id)
  if (params.search) queryParams.append('search', params.search)
  if (params.page) queryParams.append('page', params.page)
  if (params.pageSize) queryParams.append('pageSize', params.pageSize)
  if (params.sortBy) queryParams.append('sortBy', params.sortBy)
  if (params.sortDirection) queryParams.append('sortDirection', params.sortDirection)
  if (params.umbrella) queryParams.append('umbrella', params.umbrella)
  return queryParams
}

const accountingApi = {
  getEntries: (params = {}) => {
    const queryParams = accountingEntriesQueryParams(params)
    const queryString = queryParams.toString()
    return service.get(`/api/app/v1/accounting/entries${queryString ? `?${queryString}` : ''}`)
  },

  /** Mêmes filtres que getEntries ; réponse `{ entry_ids, total, capped }`. */
  getEntryIdsForFilters: (params = {}) => {
    const queryParams = accountingEntriesQueryParams(params)
    queryParams.append('id_only', '1')
    const queryString = queryParams.toString()
    return service.get(`/api/app/v1/accounting/entries?${queryString}`)
  },

  exportNapta: (teamId, entryIds) => {
    return service.post('/api/app/v1/accounting/export-napta', {
      team_id: teamId,
      entry_ids: entryIds,
    })
  },

  createEntry: (data, teamId) => {
    return service.post('/api/app/v1/accounting/entries', { ...data, team_id: teamId || data?.team_id })
  },

  updateEntryStatus: (id, status, teamId) => {
    return service.patch(`/api/app/v1/accounting/entries/${id}`, { status, team_id: teamId })
  },

  bulkUpdateStatus: (entryIds, status, teamId) => {
    return service.post('/api/app/v1/accounting/entries/bulk', { entryIds, status, team_id: teamId })
  },

  generatePaymentFile: (entryIds, teamId) => {
    return service.post('/api/app/v1/accounting/payment-file', { entryIds, team_id: teamId })
  },

  generateSepaPaymentFile: (entryIds, teamId) => {
    return service.post('/api/app/v1/accounting/sepa-payment-file', { entryIds, team_id: teamId }, {
      responseType: 'blob',
    })
  },

  generatePaymentFiles: async (entryIds, teamId) => {
    try {
      console.log('[accountingService] Generating payment files for entryIds:', entryIds)
      const res = await service.post('/api/app/v1/accounting/payment-files', { entryIds, team_id: teamId })
      // Vérifier d'abord si c'est un blob ou une string (fichier direct)
      const contentType = res?.headers?.['content-type'] || res?.headers?.['Content-Type'] || ''
      
      console.log('[accountingService] Response received:', {
        status: res?.status,
        contentType: contentType,
        headers: res?.headers,
        dataType: typeof res?.data,
        isBlob: res?.data instanceof Blob,
        isString: typeof res?.data === 'string',
        dataPreview: typeof res?.data === 'string' ? res.data.substring(0, 100) : (res?.data ? 'object' : 'null'),
        dataStructure: res?.data && typeof res?.data === 'object' ? JSON.stringify(res.data, null, 2).substring(0, 500) : (typeof res?.data === 'string' ? `string(${res.data.length} chars)` : 'null'),
      })
      const isBlob = res?.data instanceof Blob
      const isString = typeof res?.data === 'string'
      const isFile = contentType.includes('text/csv') || contentType.includes('application/xml') || contentType.includes('text/xml')
      
      if (isBlob || (isFile && isString)) {
        console.log('[accountingService] File detected (blob or string):', {
          isBlob,
          isString,
          isFile,
          contentType,
          dataLength: isString ? res.data.length : (isBlob ? res.data.size : 0),
        })
        
        let blob = isBlob ? res.data : new Blob([res.data], { type: contentType || 'application/octet-stream' })
        
        // Extraire le nom de fichier depuis les headers
        const contentDisposition = res?.headers?.['content-disposition'] || res?.headers?.['Content-Disposition'] || ''
        console.log('[accountingService] Content-Disposition header:', contentDisposition)
        console.log('[accountingService] All headers:', res?.headers)
        
        let fileName = 'payment.xml'
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i)
        console.log('[accountingService] Filename match:', fileNameMatch)
        
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1].replace(/['"]/g, '')
          try {
            fileName = decodeURIComponent(fileName)
          } catch {
            // Si le décodage échoue, utiliser le nom tel quel
          }
        } else {
          // Si pas de header, déterminer le nom selon le type de contenu
          console.log('[accountingService] No filename in header, using contentType:', contentType)
          if (contentType.includes('application/xml') || contentType.includes('text/xml')) {
            fileName = 'SEPA_payment.xml'
            console.log('[accountingService] Detected XML, setting fileName to:', fileName)
          } else if (contentType.includes('text/csv') || contentType.includes('csv')) {
            fileName = 'Wise_payment.csv'
            console.log('[accountingService] Detected CSV, setting fileName to:', fileName)
          } else {
            console.log('[accountingService] Unknown contentType, keeping default:', fileName)
          }
        }
        
        console.log('[accountingService] Final extracted fileName:', fileName)
        console.log('[accountingService] Creating blob URL for file:', fileName)
        const url = window.URL.createObjectURL(blob)
        console.log('[accountingService] Blob URL created:', url)
        
        const returnValue = {
          success: true,
          data: {
            blobUrl: url,
            fileName: fileName,
            filesGenerated: 1,
            filesInfo: [],
          },
        }
        console.log('[accountingService] Returning:', {
          success: returnValue.success,
          hasData: !!returnValue.data,
          hasBlobUrl: !!returnValue.data.blobUrl,
          fileName: returnValue.data.fileName,
          fullReturn: returnValue,
        })
        
        return returnValue
      }
      
      // Si c'est une réponse JSON avec plusieurs fichiers (structure: { code: 200, data: { files: [...] } })
      if (res?.data?.data?.files && Array.isArray(res.data.data.files)) {
        console.log('[accountingService] Multiple files detected:', res.data.data.files.length)
        return {
          success: true,
          data: res.data.data,
        }
      }
      
      // Si c'est une réponse JSON standard avec succès (structure: { code: 200, data: {...} })
      if (res?.data?.code === 200 && res?.data?.data) {
        console.log('[accountingService] Single JSON response with data')
        return {
          success: true,
          data: res.data.data,
        }
      }
      
      // Si c'est directement un objet avec files (structure: { files: [...] })
      if (res?.data?.files && Array.isArray(res.data.files)) {
        console.log('[accountingService] Files array found directly in data')
        return {
          success: true,
          data: res.data,
        }
      }
      
      // Si c'est une erreur JSON (code !== 200)
      if (res?.data?.code && res?.data?.code !== 200) {
        console.log('[accountingService] Error response:', res.data)
        return {
          success: false,
          error: res.data.message || 'Failed to generate payment files',
        }
      }
      
      // Par défaut, retourner la réponse telle quelle
      console.log('[accountingService] Default case, returning data as-is:', res?.data)
      return {
        success: true,
        data: res?.data,
      }
    } catch (error) {
      console.error('accountingService.generatePaymentFiles error', error)
      
      // Si c'est une erreur avec un message JSON
      if (error?.response?.data) {
        const errorData = error.response.data
        
        // Si c'est un objet avec un message (erreur 400 du backend)
        if (typeof errorData === 'object' && errorData !== null) {
          if (errorData.message) {
            return {
              success: false,
              error: errorData.message,
            }
          }
          // Si c'est un objet avec code et message
          if (errorData.code && errorData.message) {
            return {
              success: false,
              error: errorData.message,
            }
          }
        }
        
        // Si c'est une string (erreur JSON parsée)
        if (typeof errorData === 'string') {
          try {
            const parsed = JSON.parse(errorData)
            if (parsed.message) {
              return {
                success: false,
                error: parsed.message,
              }
            }
          } catch {
            // Ce n'est pas du JSON, utiliser le message tel quel si c'est court
            if (errorData.length < 200) {
              return {
                success: false,
                error: errorData,
              }
            }
          }
        }
      }
      
      // Message d'erreur par défaut
      const defaultMessage = error?.response?.data?.message || 
                            error?.message || 
                            'Failed to generate payment files. Please ensure all freelancers have bank account information configured.'
      
      return {
        success: false,
        error: defaultMessage,
      }
    }
  },

  /** @param {string} entryId - @param {{ email?: string }} [body] - optional recipient when multiple accounting emails */
  sendToAccounting: (entryId, body = {}) => {
    return service.post(`/api/app/v1/accounting/entries/${entryId}/send`, body)
  },

  bulkSendToAccounting: (body = {}) => {
    return service.post('/api/app/v1/accounting/entries/bulk/send', body, {
      timeout: 10 * 60 * 1000,
    })
  },
}

/** @param {unknown} freelancers - embed Supabase (objet ou tableau) */
function freelancerRowFromEmbed(freelancers) {
  if (!freelancers) return null
  return Array.isArray(freelancers) ? freelancers[0] ?? null : freelancers
}

/** Tag équipe dont le nom est exactement « BPI » (insensible à la casse / espaces). */
function computeHasBpiTeamTag(freelancerRow) {
  if (!freelancerRow || typeof freelancerRow !== 'object') return false
  const ptt = freelancerRow.profile_team_tags
  const rows = Array.isArray(ptt) ? ptt : []
  for (const row of rows) {
    const tt = row?.team_tags
    const tag = Array.isArray(tt) ? tt[0] : tt
    const name = tag && typeof tag === 'object' && 'name' in tag ? tag.name : null
    if (typeof name === 'string' && name.trim().toUpperCase() === 'BPI') return true
  }
  return false
}

function firstEmbed(value) {
  if (!value) return null
  return Array.isArray(value) ? value[0] ?? null : value
}

function legalEntityTypeFromFreelancerRow(freelancerRow) {
  if (!freelancerRow || typeof freelancerRow !== 'object') return null
  const profile = firstEmbed(freelancerRow.freelancer_profiles)
  return profile?.legal_entity_type ?? freelancerRow.legal_entity_type ?? null
}

function normalizeElectronicInvoiceMeta(activityReportData) {
  if (!activityReportData || typeof activityReportData !== 'object') return null

  const job = firstEmbed(activityReportData.invoice_emission_jobs)
  const invoice = firstEmbed(activityReportData.invoices)
  if (!job && !invoice) return null

  return {
    isElectronic: true,
    emissionJobStatus: job?.status ?? null,
    hasDocument: !!(invoice?.facturx_pdf_key || invoice?.xml_key),
    emissionCompletedAt: job?.completed_at ?? null,
  }
}

const normalizeAccountingEntry = (payload) => {
  if (!payload) return null

  // Debug: log the payload structure
  if (!payload.activity_reports && !payload.activityReport) {
    console.warn('[normalizeAccountingEntry] Missing activity_reports in payload:', {
      id: payload.id,
      activity_report_id: payload.activity_report_id,
      keys: Object.keys(payload),
      has_activity_reports: !!payload.activity_reports,
      has_activityReport: !!payload.activityReport,
    })
  }

  // Handle both possible key names (Supabase can return either)
  const activityReportData = payload.activity_reports || payload.activityReport

  return {
    id: payload.id,
    activityReportId: payload.activity_report_id,
    type: payload.type,
    status: payload.status,
    amountExclVAT: parseFloat(payload.amount_excl_vat || 0),
    vatAmount: parseFloat(payload.vat_amount || 0),
    amountInclVAT: parseFloat(payload.amount_incl_vat || 0),
    currency: payload.currency || 'EUR',
    sentToAccountingAt: payload.sent_to_accounting_at || null,
    electronicInvoice: activityReportData ? normalizeElectronicInvoiceMeta(activityReportData) : null,
    exportedToNaptaAt: payload.exported_to_napta_at || null,
    paidAt: payload.paid_at || null,
    paymentFileUrl: payload.payment_file_url,
    paymentFileKey: payload.payment_file_key,
    activityReport: activityReportData
      ? {
          id: activityReportData.id,
          reportMonth: activityReportData.report_month,
          invoiceAmountExclVAT: activityReportData.invoice_amount_excl_vat != null ? parseFloat(activityReportData.invoice_amount_excl_vat) : null,
          invoicingOptions: activityReportData.invoicing_options ?? null,
          mission: activityReportData.missions
            ? {
                id: activityReportData.missions.id,
                name: activityReportData.missions.name || null,
                purchaseOrder: activityReportData.missions.purchase_order || null,
                startDate: activityReportData.missions.start_date || null,
                endDate: activityReportData.missions.end_date || null,
              }
            : null,
          purchaseOrder: activityReportData.missions?.purchase_order || null,
          freelancer: (() => {
            const fr = freelancerRowFromEmbed(activityReportData.freelancers)
            if (!fr) return null
            return {
              id: fr.id,
              firstName: fr.first_name,
              lastName: fr.last_name,
              email: fr.email,
              avatar: fr.avatar ?? null,
              name: `${fr.first_name || ''} ${fr.last_name || ''}`.trim() || fr.email,
              legalEntityType: legalEntityTypeFromFreelancerRow(fr),
              hasBpiTeamTag: computeHasBpiTeamTag(fr),
            }
          })(),
          referent: (() => {
            // Prioriser le référent de l'activity_report, sinon celui de la mission
            // Supabase peut retourner un tableau ou un objet unique selon la relation
            let referentData = activityReportData.referents || activityReportData.missions?.referents
            if (referentData) {
              // Si c'est un tableau, prendre le premier élément
              if (Array.isArray(referentData)) {
                referentData = referentData[0]
              }
              if (referentData && referentData.id) {
                return {
                  id: referentData.id,
                  firstName: referentData.first_name,
                  lastName: referentData.last_name,
                  email: referentData.email,
                  name: `${referentData.first_name || ''} ${referentData.last_name || ''}`.trim() || referentData.email,
                }
              }
            }
            return null
          })(),
        }
      : null,
    createdAt: payload.created_at,
    updatedAt: payload.updated_at,
  }
}

export const accountingService = {
  async getEntries(filters = {}) {
    try {
      const res = await accountingApi.getEntries(filters)
      const body = res?.data
      const apiCode = typeof body?.code === 'number' ? body.code : res?.status

      if (apiCode !== 200) {
        return {
          success: false,
          error: body?.message || 'Failed to load accounting entries',
        }
      }

      const payload = body?.data ?? body
      const entries = Array.isArray(payload?.entries) ? payload.entries : []

      const normalizedEntries = entries.map(normalizeAccountingEntry).filter(entry => entry !== null)

      return {
        success: true,
        data: normalizedEntries,
        total: payload?.total ?? normalizedEntries.length,
      }
    } catch (error) {
      console.error('accountingService.getEntries error', error)
      return { success: false, error: error?.message || 'Unknown error' }
    }
  },

  /** Tous les IDs d’entrées comptables pour les filtres courants (sélection masse). */
  async fetchEntryIdsForFilters(filters = {}) {
    try {
      const res = await accountingApi.getEntryIdsForFilters(filters)
      const payload = res?.data?.data || res?.data
      const entryIds = Array.isArray(payload?.entry_ids)
        ? payload.entry_ids.map((id) => String(id)).filter((id) => id.length > 0)
        : []
      return {
        success: true,
        entryIds,
        total: typeof payload?.total === 'number' ? payload.total : entryIds.length,
        capped: Boolean(payload?.capped),
      }
    } catch (error) {
      console.error('accountingService.fetchEntryIdsForFilters error', error)
      const message = error?.response?.data?.message || error?.message || 'Unknown error'
      return { success: false, error: message }
    }
  },

  async createEntry(data, teamId) {
    try {
      const res = await accountingApi.createEntry(data, teamId)
      const payload = res?.data?.data || res?.data
      return {
        success: true,
        data: normalizeAccountingEntry(payload),
      }
    } catch (error) {
      console.error('accountingService.createEntry error', error)
      return { success: false, error: error?.message || 'Unknown error' }
    }
  },

  async updateEntryStatus(id, status, teamId) {
    try {
      const res = await accountingApi.updateEntryStatus(id, status, teamId)
      const payload = res?.data?.data || res?.data
      return {
        success: true,
        data: normalizeAccountingEntry(payload),
      }
    } catch (error) {
      console.error('accountingService.updateEntryStatus error', error)
      return { success: false, error: error?.message || 'Unknown error' }
    }
  },

  async bulkUpdateStatus(entryIds, status, teamId) {
    try {
      const res = await accountingApi.bulkUpdateStatus(entryIds, status, teamId)
      return {
        success: true,
        data: res?.data?.data || res?.data,
      }
    } catch (error) {
      console.error('accountingService.bulkUpdateStatus error', error)
      return { success: false, error: error?.message || 'Unknown error' }
    }
  },

  async generatePaymentFile(entryIds, teamId) {
    try {
      const res = await accountingApi.generatePaymentFile(entryIds, teamId)
      const payload = res?.data?.data || res?.data
      return {
        success: true,
        data: payload,
      }
    } catch (error) {
      console.error('accountingService.generatePaymentFile error', error)
      return { success: false, error: error?.message || 'Unknown error' }
    }
  },

  async generateSepaPaymentFile(entryIds, teamId) {
    try {
      const res = await accountingApi.generateSepaPaymentFile(entryIds, teamId)
      
      // Vérifier si la réponse est une erreur (peut arriver avec responseType: 'blob')
      if (res.status && res.status >= 400) {
        // Si c'est une erreur, essayer de lire le message d'erreur depuis le blob
        try {
          const errorText = await res.data.text()
          const errorJson = JSON.parse(errorText)
          return { success: false, error: errorJson.message || 'Unknown error' }
        } catch {
          return { success: false, error: 'Failed to generate payment file' }
        }
      }
      
      // La réponse est un blob (fichier XML)
      const blob = res.data
      if (!(blob instanceof Blob)) {
        return { success: false, error: 'Invalid response format' }
      }
      
      const contentDisposition = res.headers['content-disposition'] || res.headers['Content-Disposition'] || ''
      const fileNameMatch = contentDisposition.match(/filename="(.+)"/)
      const fileName = fileNameMatch ? fileNameMatch[1] : `sepa-payment-${Date.now()}.xml`
      
      // Créer une URL blob pour le téléchargement
      const url = window.URL.createObjectURL(blob)
      
      return {
        success: true,
        data: {
          blobUrl: url,
          fileName: fileName,
        },
      }
    } catch (error) {
      console.error('accountingService.generateSepaPaymentFile error', error)
      // Si c'est une erreur blob, essayer d'extraire le message
      if (error.response?.data instanceof Blob) {
        try {
          const errorText = await error.response.data.text()
          const errorJson = JSON.parse(errorText)
          return { success: false, error: errorJson.message || 'Unknown error' }
        } catch {
          return { success: false, error: error?.message || 'Failed to generate payment file' }
        }
      }
      return { success: false, error: error?.response?.data?.message || error?.message || 'Unknown error' }
    }
  },

  async generatePaymentFiles(entryIds, teamId) {
    try {
      console.log('[accountingService] generatePaymentFiles called in accountingService wrapper')
      // Utiliser directement la fonction async de accountingApi qui gère déjà les strings et blobs
      const result = await accountingApi.generatePaymentFiles(entryIds, teamId)
      console.log('[accountingService] Result from accountingApi.generatePaymentFiles:', {
        success: result?.success,
        hasData: !!result?.data,
        hasBlobUrl: !!result?.data?.blobUrl,
        hasFiles: !!result?.data?.files,
        resultKeys: result ? Object.keys(result) : [],
        fullResult: result,
      })
      return result
    } catch (error) {
      console.error('[accountingService] generatePaymentFiles error in wrapper:', error)
      return { success: false, error: error?.message || 'Unknown error' }
    }
  },

  async sendToAccounting(entryId, body = {}) {
    try {
      const res = await accountingApi.sendToAccounting(entryId, body)
      const payload = res?.data?.data || res?.data
      return {
        success: true,
        data: payload,
      }
    } catch (error) {
      console.error('accountingService.sendToAccounting error', error)
      return { success: false, error: error?.message || 'Unknown error' }
    }
  },

  async bulkSendToAccounting(body = {}) {
    try {
      const res = await accountingApi.bulkSendToAccounting(body)
      const payload = res?.data?.data || res?.data
      return {
        success: true,
        data: payload,
      }
    } catch (error) {
      console.error('accountingService.bulkSendToAccounting error', error)
      const message = error?.response?.data?.message || error?.message || 'Unknown error'
      return { success: false, error: message }
    }
  },

  async exportNapta(teamId, entryIds) {
    try {
      const res = await accountingApi.exportNapta(teamId, entryIds)
      const payload = res?.data?.data || res?.data
      const rows = payload?.rows || []
      return { success: true, data: rows }
    } catch (error) {
      console.error('accountingService.exportNapta error', error)
      const message = error?.response?.data?.message || error?.message || 'Export failed'
      return { success: false, error: message }
    }
  },
}

export default accountingService

